import { ApplicationError } from "../../models/errors/application-error";
import { ApplicationFileList } from "../../models/organisation/application-file-list";
import { ApplicationFile } from "../../models/organisation/application-file";
import { components } from "../../external/schema/order-link-api-schema";
import client from "../../external/open-api";
import IFileService from "./file-service-interface";
import BaseService from "../base-service";

export default class FileService extends BaseService implements IFileService {
  async getFiles(
    currentPosition: number,
    fetchNext: number,
    fetchAll: boolean,
    searchText: string
  ): Promise<ApplicationFileList> {
    const { data, error } = await this.withRetry(() => client.GET("/v{version}/api/file", {
      params: {
        path: { version: "1" },
        query: {
          "Paging.CurrentPosition": currentPosition,
          "Paging.FetchNext": fetchNext,
          "Paging.FetchAll": fetchAll,
          SearchText: searchText,
        },
      },
    }));

    if (!data?.data || !data?.success || error) {
      throw new ApplicationError("Failed to fetch users");
    }

    return {
      files: this.mapToApplicationFiles(data.data.files ?? []),
      loading: false,
      count: data.data?.count || 0,
      currentPosition: data.data?.currentPosition || 0,
    };
  }
  async deleteFile(name: string): Promise<void> {
    const { data, error } = await this.withRetry(() => client.DELETE("/v{version}/api/file", {
      params: {
        path: { version: "1" },
      },
      body: {
        name: name,
      },
    }));

    if (!data?.success || error) {
      throw new ApplicationError("Failed to delete the file");
    }
  }

  async uploadFile(file: File): Promise<ApplicationFile> {
    const { data, error } = await this.withRetry(() => client.POST("/v{version}/api/file", {
      params: {
        path: { version: "1" },
      },
      body: {}, // bypass type-checking by not passing typed fields here
      bodySerializer() {
        const formData = new FormData();
        formData.append("ContentType", file.type);
        formData.append("FileName", file.name);
        formData.append("Name", file.name);
        formData.append("Length", file.size.toString());
        formData.append("File", file); // yes, this works
        return formData;
      },
    }));

    if (!data?.success || error || !data.data) {
      throw new ApplicationError("Failed to upload file");
    }
    return this.mapToApplicationFile(data.data);
  }

  private mapToApplicationFile(
    file: components["schemas"]["FileDTO"]
  ): ApplicationFile {
    return {
      name: file.name ?? "",
      displayName: FileService.removeGuidFromFileName(file.name ?? ""),
      size: file.size ?? 0,
      fullPath: file.fullPath ?? "",
    };
  }
  private static removeGuidFromFileName(fileName: string): string {
    const guidRegex =
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}-/;
    return fileName.replace(guidRegex, "");
  }

  private mapToApplicationFiles(
    files: components["schemas"]["FileDTO"][]
  ): ApplicationFile[] {
    return files.map(this.mapToApplicationFile);
  }
}
